// src/AppHome.js
import { useCallback, useMemo, useState } from 'react';
import { API_URL } from '../config';
import ProtectedRoute from './ProtectedRoute';
import Sidebar from './Sidebar';
import FindInvestors from './app-components/FindInvestors';
import CRM from './app-components/CRM';
import Deals from './app-components/Deals';
import Statistics from './app-components/Statistics';
import AIFunding from './app-components/AIFunding';
import InvestorProfileApp from './app-components/investor-profile/investorProfileApp';
import Dashboard from './app-components/Dashboard'; // Ensure you have a Dashboard component
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'; // Import useLocation
import { useInvestorsSearch } from '../hooks/useInvestorsSearch';
import { MEMBERSHIP_ROUTE_ACCESS } from '../hooks/membership';
import { useMembershipAccess } from '../hooks/membership';
import { CRMProvider } from '../hooks/crm';
import { readPersistedInvestorFilters } from './app-components/find-investors-modules/utils';

const AppHome = () => {
  const itemsPerPage = 10;
  const [totalDealsCount, setTotalDealsCount] = useState(0);

  // State variables for deals filtering
  const [dealsFilters, setDealsFilters] = useState({
    selectedRounds: [],
    investmentAmount: null,
    keyword: '',
    year: '',
  });
  const [dealsTotalResults, setDealsTotalResults] = useState(0);
  const [dealsCurrentPage, setDealsCurrentPage] = useState(1);

  const location = useLocation(); // Get the current location
  const initialPage = Number(new URLSearchParams(location.search).get('p') || 1);
  const isFindInvestorsRoute = /\/app\/find-investors\/?$/.test(location.pathname);
  const {
    canUseInvestmentInfoFilter: canAccessInvestmentInfo,
    loading: membershipLoading,
  } = useMembershipAccess();
  const initialInvestorFilters = useMemo(
    () => readPersistedInvestorFilters(location.search),
    [location.search]
  );

  const buildInvestorRequestParams = useCallback(({ page, pageSize, filters }) => ({
    page,
    limit: pageSize,
    summary: 1,
    preferDealsFirst: true,
    name: filters.keyword || undefined,
    country: filters.mainOfficeLocation || undefined,
    types: filters.selectedTypes.length > 0 ? filters.selectedTypes.join(',') : undefined,
    industries: filters.industry || undefined,
    onlyWithInvestmentsInfo:
      canAccessInvestmentInfo && filters.onlyWithInvestmentsInfo ? 'true' : undefined,
  }), [canAccessInvestmentInfo]);

  const mapInvestorResponse = useCallback((data) => ({
    items: data?.investors || [],
    totalResults: data?.totalCount || 0,
    meta: {
      membershipType: data?.membershipType || null,
    },
  }), []);

  const {
    items: filteredInvestors,
    totalResults,
    isLoading,
    currentPage,
    setPage,
    applyFilters,
  } = useInvestorsSearch({
    endpoint: `${API_URL}/investors`,
    pageSize: itemsPerPage,
    initialPage,
    initialFilters: initialInvestorFilters,
    pageParam: 'p',
    syncPageToUrl: true,
    replaceHistory: false,
    withCredentials: true,
    enabled: isFindInvestorsRoute && !membershipLoading,
    buildRequestParams: buildInvestorRequestParams,
    mapResponse: mapInvestorResponse,
  });

  // Handle filter change
  const handleFilterChange = useCallback((filters, resetPage = true) => {
    applyFilters(filters, { resetPage });
  }, [applyFilters]);

  // Handle filter change for deals
  const handleDealsFilterChange = useCallback((filters) => {
    setDealsFilters(filters);
    setDealsCurrentPage(1); // Reset to first page when filters change
  }, []);

  return (
    <CRMProvider>
      <div className="px-16 mt-6 mb-20 flex h-full">
        <div className="w-[22%]">
          <Sidebar
            handleFilterChange={handleFilterChange}
            handleDealsFilterChange={handleDealsFilterChange} // Pass the new handler
            filteredCount={filteredInvestors.length}
            totalResults={totalResults}
            totalDealsCount={totalDealsCount}
            isLoading={isLoading}
          />
        </div>
        <main className="max-w-5xl w-full mx-auto">
          <Routes>
            <Route path="/" element={<Navigate to="dashboard" replace />} />

            {/* Dashboard is accessible to all users */}
            <Route path="dashboard" element={<Dashboard />} />

            {/* Find Investors accessible to all users */}
            <Route
              path="find-investors"
              element={
                <ProtectedRoute
                  allowedMemberships={MEMBERSHIP_ROUTE_ACCESS.findInvestors}
                ><FindInvestors
                  filteredInvestors={filteredInvestors}
                  totalResults={totalResults} // Passing totalResults to the component
                  currentPage={currentPage}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setPage}
                  isLoading={isLoading}
                /></ProtectedRoute>
              }
            />

            {/* Protected Routes */}
            <Route
              path="crm"
              element={
                <ProtectedRoute
                  allowedMemberships={MEMBERSHIP_ROUTE_ACCESS.crm}
                >
                  <CRM />
                </ProtectedRoute>
              }
            />
            <Route
              path="deals"
              element={
                <ProtectedRoute allowedMemberships={MEMBERSHIP_ROUTE_ACCESS.deals}>
                  <Deals
                    setTotalDealsCount={setTotalDealsCount}
                    dealsFilters={dealsFilters}
                    dealsCurrentPage={dealsCurrentPage}
                    setDealsCurrentPage={setDealsCurrentPage}
                    setDealsTotalResults={setDealsTotalResults}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="statistics/*"
              element={
                <ProtectedRoute allowedMemberships={MEMBERSHIP_ROUTE_ACCESS.statistics}>
                  <Statistics />
                </ProtectedRoute>
              }
            />
            <Route
              path="ai-funding"
              element={
                <ProtectedRoute allowedMemberships={MEMBERSHIP_ROUTE_ACCESS.aiFunding}>
                  <AIFunding />
                </ProtectedRoute>
              }
            />

            {/* Investor Profile accessible to all users */}
            <Route path="investor-profile/:investorId" element={<InvestorProfileApp />} />
          </Routes>
        </main>
      </div>
    </CRMProvider>
  );
};

export default AppHome;
